import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Send, Save, X, CalendarRange, Clock, BookOpen, Users, GraduationCap, Download, Check, Upload, Building2, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { SearchSelect, MultiSearchSelect } from '@/components/SearchSelect';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const cellKey = (bid, day, pid) => `${bid}__${day}__${pid}`;
const hoursOf = (p) => {
  try {
    const [sh, sm] = p.start_time.split(':').map(Number);
    const [eh, em] = p.end_time.split(':').map(Number);
    return Math.max(0, (eh * 60 + em) - (sh * 60 + sm)) / 60;
  } catch { return 0; }
};

export default function TimetablePage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SuperAdmin';
  const myCentre = user?.centre || '';

  const [centres, setCentres] = useState([]);
  const [pickedCentre, setPickedCentre] = useState('');
  const activeCentre = isSuperAdmin ? pickedCentre : myCentre;

  const [tab, setTab] = useState('timetables');
  const [subjects, setSubjects] = useState([]);
  const [batches, setBatches] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [courses, setCourses] = useState([]);
  const [faculty, setFaculty] = useState([]);
  const [timetables, setTimetables] = useState([]);
  const [builder, setBuilder] = useState(null); // null=list, else {id?, name, week_start, batch_ids, entries, status}
  const [activeBatchTab, setActiveBatchTab] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const csvInputRef = useRef(null);
  const [conflicts, setConflicts] = useState([]);
  const [hoursDashboard, setHoursDashboard] = useState([]);
  const [hoursDashboardLoading, setHoursDashboardLoading] = useState(false);
  const [expandedHoursRows, setExpandedHoursRows] = useState(new Set());

  useEffect(() => {
    if (isSuperAdmin) {
      api.getCentres().then(r => {
        const list = r.data || [];
        setCentres(list);
        setPickedCentre(prev => prev || list[0] || '');
      }).catch(() => {});
    }
  }, [isSuperAdmin]);

  useEffect(() => { api.getCourses().then(r => setCourses(r.data || [])).catch(() => {}); }, []);

  const loadAll = useCallback(async () => {
    if (isSuperAdmin && !activeCentre) {
      setSubjects([]); setBatches([]); setPeriods([]); setFaculty([]); setTimetables([]);
      return;
    }
    try {
      const [s, b, p, f, t] = await Promise.all([
        api.ttSubjects(activeCentre), api.ttBatches(activeCentre), api.ttPeriods(activeCentre),
        api.ttFacultyOptions(activeCentre), api.ttTimetables(activeCentre),
      ]);
      setSubjects(s.data || []); setBatches(b.data || []);
      setPeriods((p.data || []).sort((a, b2) => (a.order || 0) - (b2.order || 0)));
      setFaculty(f.data || []); setTimetables(t.data || []);
    } catch (e) { toast.error('Failed to load timetable data'); }
  }, [activeCentre, isSuperAdmin]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Live faculty double-booking check — re-runs (debounced) on every grid edit so clashes
  // highlight immediately, without waiting for Save/Publish.
  useEffect(() => {
    if (!builder?.id) { setConflicts([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.ttCheckConflicts(builder.id, entriesArray(builder));
        setConflicts(data.conflicts || []);
      } catch { /* silent — non-blocking */ }
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [builder]);

  useEffect(() => {
    if (tab !== 'hours' || !activeCentre) return;
    setHoursDashboardLoading(true);
    api.ttHoursDashboard(activeCentre)
      .then(r => setHoursDashboard(r.data.rows || []))
      .catch(() => toast.error('Failed to load hours dashboard'))
      .finally(() => setHoursDashboardLoading(false));
  }, [tab, activeCentre]);

  // ---------- Setup CRUD helpers ----------
  const [newSub, setNewSub] = useState({ shortcode: '', name: '' });
  const [newBatch, setNewBatch] = useState({ name: '', course_id: '' });
  const [newPeriod, setNewPeriod] = useState({ label: '', start_time: '', end_time: '', is_break: false, break_label: 'LUNCH' });
  const [newCourse, setNewCourse] = useState({ name: '', code: '' });
  const [edit, setEdit] = useState(null); // { kind, id, fields }
  const [dl, setDl] = useState({ tid: '', mode: 'week', week_start: '', faculty_id: '', batch_id: '', layout: 'batch' });

  const requireCentre = () => {
    if (isSuperAdmin && !activeCentre) { toast.error('Pick a centre to manage first'); return false; }
    return true;
  };

  const saveEdit = async () => {
    const { kind, id, fields } = edit;
    try {
      if (kind === 'period') await api.ttUpdatePeriod(id, fields);
      else if (kind === 'subject') await api.ttUpdateSubject(id, fields);
      else if (kind === 'course') await api.updateCourse(id, fields.name, fields.code);
      else if (kind === 'batch') await api.ttUpdateBatch(id, fields);
      toast.success('Updated'); setEdit(null); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const downloadTT = async (fmt) => {
    if (!dl.tid) return toast.error('Select a timetable to download');
    try {
      const params = { format: fmt, mode: dl.mode, layout: dl.layout };
      if (dl.mode === 'date' && dl.week_start) params.week_start = dl.week_start;
      if (dl.faculty_id) params.faculty_id = dl.faculty_id;
      if (dl.batch_id) params.batch_id = dl.batch_id;
      const res = await api.ttExportTimetable(dl.tid, params);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const tt = timetables.find(t => t._id === dl.tid);
      const a = document.createElement('a');
      a.href = url; a.download = `${(tt?.name || 'timetable').replace(/\s+/g, '_')}_${dl.layout === 'day' ? 'daywise' : dl.mode}.${fmt === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Download failed'); }
  };

  const addSubject = async () => {
    if (!requireCentre()) return;
    if (!newSub.shortcode.trim()) return toast.error('Enter subject shortcode');
    try { await api.ttCreateSubject({ ...newSub, centre: activeCentre }); setNewSub({ shortcode: '', name: '' }); toast.success('Subject added'); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const addBatch = async () => {
    if (!requireCentre()) return;
    if (!newBatch.name.trim()) return toast.error('Enter batch name');
    try { await api.ttCreateBatch({ ...newBatch, centre: activeCentre }); setNewBatch({ name: '', course_id: '' }); toast.success('Batch added'); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const addPeriod = async () => {
    if (!requireCentre()) return;
    if (!newPeriod.label.trim() || !newPeriod.start_time || !newPeriod.end_time) return toast.error('Fill label, start & end time');
    try { await api.ttCreatePeriod({ ...newPeriod, order: periods.length, centre: activeCentre }); setNewPeriod({ label: '', start_time: '', end_time: '', is_break: false, break_label: 'LUNCH' }); toast.success('Session slot added'); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const addCourse = async () => {
    if (!newCourse.name.trim()) return toast.error('Enter course name');
    try { await api.createCourse(newCourse.name, newCourse.code); setNewCourse({ name: '', code: '' }); toast.success('Course added'); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // ---------- Builder ----------
  const startNew = () => {
    if (!requireCentre()) return;
    if (periods.length === 0) { toast.error('Create session slots first (Setup tab)'); setTab('setup'); return; }
    if (batches.length === 0) { toast.error('Create batches first (Setup tab)'); setTab('setup'); return; }
    setBuilder({ name: '', week_start: '', batch_ids: [], entries: {}, status: 'Draft' });
    setActiveBatchTab(''); setCsvFile(null); setConflicts([]);
  };
  const openEdit = async (tt) => {
    try {
      const { data } = await api.ttGetTimetable(tt._id);
      const entries = {};
      (data.entries || []).forEach(e => {
        const faculty_ids = e.faculty_ids && e.faculty_ids.length ? e.faculty_ids : (e.faculty_id ? [e.faculty_id] : []);
        entries[cellKey(e.batch_id, e.day, e.period_id)] = { subject_id: e.subject_id || '', faculty_ids, room: e.room || '' };
      });
      const bids = data.batch_ids || [];
      setBuilder({ id: data._id, name: data.name, week_start: data.week_start || '', batch_ids: bids, entries, status: data.status });
      setActiveBatchTab(bids[0] || ''); setCsvFile(null); setConflicts([]);
    } catch { toast.error('Failed to open timetable'); }
  };
  const toggleBuilderBatch = (bid) => {
    setBuilder(b => {
      const already = b.batch_ids.includes(bid);
      const batch_ids = already ? b.batch_ids.filter(x => x !== bid) : [...b.batch_ids, bid];
      return { ...b, batch_ids };
    });
    setActiveBatchTab(prev => prev || bid);
  };
  const setCell = (bid, day, pid, field, val) => {
    setBuilder(b => {
      const k = cellKey(bid, day, pid);
      const cur = b.entries[k] || { subject_id: '', faculty_ids: [], room: '' };
      return { ...b, entries: { ...b.entries, [k]: { ...cur, [field]: val } } };
    });
  };
  const entriesArray = (b) => {
    const arr = [];
    b.batch_ids.forEach(bid => periods.filter(p => !p.is_break).forEach(p => DAYS.forEach(day => {
      const c = b.entries[cellKey(bid, day, p._id)];
      if (c && (c.subject_id || (c.faculty_ids && c.faculty_ids.length))) arr.push({ batch_id: bid, day, period_id: p._id, subject_id: c.subject_id || null, faculty_ids: c.faculty_ids || [], room: c.room || '' });
    })));
    return arr;
  };
  const facultyHoursPreview = (b) => {
    const hrs = {};
    entriesArray(b).forEach(e => {
      const p = periods.find(x => x._id === e.period_id);
      if (!p) return;
      (e.faculty_ids || []).forEach(fid => { hrs[fid] = +((hrs[fid] || 0) + hoursOf(p)).toFixed(2); });
    });
    return hrs;
  };
  const saveBuilder = async (publishAfter = false) => {
    if (!builder.name.trim()) return toast.error('Enter a timetable name');
    if (builder.batch_ids.length === 0) return toast.error('Select at least one batch to schedule');
    const payload = { name: builder.name.trim(), centre: activeCentre, batch_ids: builder.batch_ids, week_start: builder.week_start || null, entries: entriesArray(builder) };
    try {
      let id = builder.id;
      let renotified = false;
      if (id) { const r = await api.ttUpdateTimetable(id, payload); renotified = r.data?.renotified; }
      else { const { data } = await api.ttCreateTimetable(payload); id = data._id; }
      if (publishAfter) {
        await api.ttPublishTimetable(id);
        toast.success('Timetable published — faculty notified');
        setBuilder(b => ({ ...b, id, status: 'Published' }));
      } else {
        toast.success(renotified ? 'Updated — affected faculty re-notified' : (builder.id ? 'Timetable saved' : 'Timetable created — you can now download the bulk-fill template or fill the grid below'));
        setBuilder(b => ({ ...b, id }));
      }
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };
  const deleteTimetable = async (id) => {
    if (!window.confirm('Delete this timetable?')) return;
    try { await api.ttDeleteTimetable(id); toast.success('Deleted'); setBuilder(null); loadAll(); } catch { toast.error('Failed'); }
  };
  const publishFromList = async (id) => {
    try { await api.ttPublishTimetable(id); toast.success('Published — faculty notified'); loadAll(); } catch { toast.error('Failed'); }
  };

  const downloadCsvTemplate = async () => {
    if (!builder.id) return toast.error('Save the timetable first (Save Draft) to unlock the template');
    try {
      const res = await api.ttCsvTemplate(builder.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `${(builder.name || 'timetable').replace(/\s+/g, '_')}_template.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to download template'); }
  };
  const uploadCsv = async () => {
    if (!builder.id) return toast.error('Save the timetable first (Save Draft) to unlock upload');
    if (!csvFile) return toast.error('Choose the filled .xlsx template first');
    const formData = new FormData();
    formData.append('file', csvFile);
    try {
      const { data } = await api.ttCsvUpload(builder.id, formData);
      toast.success(data.message || 'Template imported', data.conflicts?.length ? { duration: 9000 } : undefined);
      const { data: fresh } = await api.ttGetTimetable(builder.id);
      const entries = {};
      (fresh.entries || []).forEach(e => {
        const faculty_ids = e.faculty_ids && e.faculty_ids.length ? e.faculty_ids : (e.faculty_id ? [e.faculty_id] : []);
        entries[cellKey(e.batch_id, e.day, e.period_id)] = { subject_id: e.subject_id || '', faculty_ids, room: e.room || '' };
      });
      setBuilder(b => ({ ...b, entries, batch_ids: fresh.batch_ids || b.batch_ids }));
      setConflicts(data.conflicts || []);
      setCsvFile(null);
      if (csvInputRef.current) csvInputRef.current.value = '';
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed', { duration: 8000 }); }
  };

  const facName = (id) => { const f = faculty.find(x => x.id === id); return f ? f.short_name : ''; };
  const batchName = (id) => batches.find(b => b._id === id)?.name || '—';

  const exportHoursDashboard = async (fmt) => {
    if (!requireCentre()) return;
    try {
      const res = await api.ttHoursDashboardExport(activeCentre, fmt);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `Hours_Dashboard_${activeCentre}.${fmt === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Export failed'); }
  };

  if (!isSuperAdmin && !myCentre) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="timetable-no-centre">
        <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="text-sm text-slate-500">No centre is assigned to your account yet. Ask Super Admin to assign you a centre before you can manage timetables.</p>
      </div>
    );
  }

  // ================= RENDER =================
  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6" data-testid="timetable-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-violet-600">Scheduling</p>
          <h1 className="text-3xl font-black tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Time Table Manager</h1>
        </div>
        {isSuperAdmin ? (
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-violet-600" />
            <label className="text-[10px] font-bold uppercase text-slate-400">Managing Centre</label>
            <select data-testid="tt-centre-picker" value={pickedCentre} onChange={e => { setPickedCentre(e.target.value); setBuilder(null); }} className="h-9 border border-slate-300 rounded-md px-2 text-sm min-w-[160px]">
              <option value="">— Select Centre —</option>
              {centres.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        ) : (
          <span className="text-xs font-bold bg-violet-50 text-violet-700 px-3 py-1.5 rounded-full flex items-center gap-1.5" data-testid="tt-my-centre"><Building2 className="w-3.5 h-3.5" />{myCentre}</span>
        )}
      </div>

      {isSuperAdmin && !activeCentre ? (
        <div className="text-center py-16" data-testid="tt-pick-centre-prompt"><Building2 className="w-10 h-10 text-slate-300 mx-auto mb-3" /><p className="text-sm text-slate-500">Pick a centre above to manage its timetables.</p></div>
      ) : (
      <>
      {!builder && (
        <div className="flex gap-2 border-b border-slate-200">
          {[['timetables', 'Timetables'], ['hours', 'Hours Dashboard'], ['setup', 'Setup']].map(([k, l]) => (
            <button key={k} data-testid={`tt-tab-${k}`} onClick={() => setTab(k)}
              className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px transition-colors ${tab === k ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>{l}</button>
          ))}
        </div>
      )}

      {/* ---------------- BUILDER ---------------- */}
      {builder ? (
        <div className="space-y-4" data-testid="tt-builder">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <label className="text-[10px] font-bold uppercase text-slate-400">Timetable Name</label>
              <Input data-testid="tt-name" value={builder.name} onChange={e => setBuilder({ ...builder, name: e.target.value })} placeholder="e.g. Week 1 — All Batches" className="h-9" />
            </div>
            <div className="min-w-[140px]">
              <label className="text-[10px] font-bold uppercase text-slate-400">Week Starting (optional)</label>
              <Input data-testid="tt-week-start" type="date" value={builder.week_start || ''} onChange={e => setBuilder({ ...builder, week_start: e.target.value })} className="h-9" />
            </div>
            <div className="flex gap-2">
              <Button data-testid="tt-save" onClick={() => saveBuilder(false)} variant="outline" className="h-9"><Save className="w-4 h-4 mr-1.5" />Save Draft</Button>
              <Button data-testid="tt-publish" onClick={() => saveBuilder(true)} className="h-9 bg-violet-600 hover:bg-violet-700 text-white"><Send className="w-4 h-4 mr-1.5" />{builder.status === 'Published' ? 'Save & Re-publish' : 'Publish'}</Button>
              <Button data-testid="tt-close" onClick={() => { setBuilder(null); setConflicts([]); loadAll(); }} variant="ghost" className="h-9"><X className="w-4 h-4" /></Button>
            </div>
          </div>

          {/* Batch multi-select */}
          <div>
            <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">Batches covered by this weekly schedule</label>
            <div className="flex flex-wrap gap-2 border border-slate-200 rounded-lg p-2 max-h-32 overflow-y-auto" data-testid="tt-batch-select">
              {batches.map(b => (
                <label key={b._id} data-testid={`tt-batch-opt-${b._id}`} className={`flex items-center gap-1.5 text-xs border rounded-full px-2.5 py-1 cursor-pointer ${builder.batch_ids.includes(b._id) ? 'border-violet-400 bg-violet-50 text-violet-700 font-semibold' : 'border-slate-200 text-slate-500'}`}>
                  <Checkbox checked={builder.batch_ids.includes(b._id)} onCheckedChange={() => toggleBuilderBatch(b._id)} />
                  {b.name}
                </label>
              ))}
              {batches.length === 0 && <span className="text-xs text-slate-400">No batches yet — add some in Setup.</span>}
            </div>
          </div>

          {/* Bulk-fill Excel template */}
          <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-3 space-y-2">
            <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2"><Download className="w-4 h-4 text-violet-600" />Bulk-Fill Template (Excel — day-wise, all batches together)</h3>
            {!builder.id ? (
              <p className="text-xs text-slate-500">Click <b>Save Draft</b> above first — this unlocks the template download & upload for the batches you selected.</p>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <Button data-testid="tt-csv-template" onClick={downloadCsvTemplate} variant="outline" className="h-8"><Download className="w-4 h-4 mr-1.5" />Download Template (.xlsx)</Button>
                <input ref={csvInputRef} type="file" accept=".xlsx" data-testid="tt-csv-input" onChange={e => setCsvFile(e.target.files?.[0] || null)} className="text-xs max-w-[220px]" />
                <Button data-testid="tt-csv-upload" onClick={uploadCsv} disabled={!csvFile} className="h-8 bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-40"><Upload className="w-4 h-4 mr-1.5" />Upload Filled Template</Button>
              </div>
            )}
          </div>

          {/* Faculty double-booking conflicts — live, re-checked on every grid edit or CSV import */}
          {conflicts.length > 0 && (
            <div className="rounded-lg border border-red-300 bg-red-50 p-3 space-y-2" data-testid="tt-conflicts-panel">
              <h3 className="text-sm font-bold text-red-700 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />{conflicts.length} Faculty Double-Booking Conflict{conflicts.length > 1 ? 's' : ''} — resolve manually before publishing
              </h3>
              <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                {conflicts.map((c, i) => (
                  <div key={i} data-testid={`tt-conflict-${i}`} className="text-xs bg-white border border-red-200 rounded px-2.5 py-2">
                    <span className="font-bold text-red-700">{c.faculty_name}</span> is booked for{' '}
                    <b>{c.sessions.map(s => `${s.batch_name}${s.subject_code ? ` (${s.subject_code})` : ''}`).join(' & ')}</b>{' '}
                    at the same time — <b>{c.day} · {c.period_label}</b>.
                    {c.suggested_faculty.length > 0 ? (
                      <span className="text-slate-600"> Free alternatives: {c.suggested_faculty.map(f => `${f.name}${f.teaches_subject ? ' ★ (teaches this subject)' : ''}`).join(', ')}</span>
                    ) : (
                      <span className="text-slate-400"> No free alternative faculty found for this slot.</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Per-batch grid */}
          {builder.batch_ids.length === 0 ? (
            <p className="text-xs text-amber-600">Select at least one batch above to start building the grid.</p>
          ) : (
            <>
              <div className="flex gap-1 border-b border-slate-200 overflow-x-auto" data-testid="tt-batch-tabs">
                {builder.batch_ids.map(bid => (
                  <button key={bid} data-testid={`tt-batch-tab-${bid}`} onClick={() => setActiveBatchTab(bid)}
                    className={`px-3 py-1.5 text-xs font-bold border-b-2 -mb-px whitespace-nowrap transition-colors ${activeBatchTab === bid ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>{batchName(bid)}</button>
                ))}
              </div>

              <div className="overflow-x-auto border border-slate-200 rounded-lg">
                <table className="min-w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="p-2 text-left font-bold text-slate-500 sticky left-0 bg-slate-50">Session</th>
                      {DAYS.map(d => <th key={d} className="p-2 text-center font-bold text-slate-500 min-w-[150px]">{d}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {periods.map(p => (
                      <tr key={p._id} className={`border-t border-slate-100 ${p.is_break ? 'bg-amber-50' : ''}`}>
                        <td className="p-2 align-top sticky left-0 bg-white">
                          <div className="font-bold text-slate-700">{p.label}</div>
                          <div className="text-[10px] text-slate-400">{p.start_time}–{p.end_time}</div>
                        </td>
                        {p.is_break ? (
                          <td colSpan={DAYS.length} className="p-2 text-center text-xs font-bold text-amber-700" data-testid={`break-row-${p._id}`}>
                            {p.break_label || 'LUNCH'} — no class scheduled, applies to every batch
                          </td>
                        ) : DAYS.map(d => {
                          const c = builder.entries[cellKey(activeBatchTab, d, p._id)] || {};
                          const cFids = c.faculty_ids || [];
                          const cellConflicts = cFids.length ? conflicts.filter(cf => cf.day === d && cf.period_id === p._id && cFids.includes(cf.faculty_id)) : [];
                          return (
                            <td key={d} className={`p-1 align-top ${cellConflicts.length ? 'bg-red-50' : ''}`}>
                              {cellConflicts.length > 0 && (
                                <div
                                  data-testid={`cell-conflict-${activeBatchTab}-${d}-${p._id}`}
                                  title={cellConflicts.map(conflict => `${conflict.faculty_name} also booked for: ${conflict.sessions.filter(s => s.batch_id !== activeBatchTab).map(s => s.batch_name).join(', ')}. Free alternatives: ${conflict.suggested_faculty.map(f => f.name).join(', ') || 'none'}`).join(' | ')}
                                  className="flex items-center gap-1 text-[9px] font-bold text-red-600 mb-1 cursor-help"
                                >
                                  <AlertTriangle className="w-3 h-3 shrink-0" /> Clash
                                </div>
                              )}
                              <div className="mb-1">
                                <SearchSelect testId={`cell-subject-${activeBatchTab}-${d}-${p._id}`} value={c.subject_id || ''}
                                  onChange={(v) => setCell(activeBatchTab, d, p._id, 'subject_id', v)} placeholder="Subject"
                                  options={subjects.map(s => ({ value: s._id, label: s.shortcode, sub: s.name }))} />
                              </div>
                              <div className="mb-1">
                                <MultiSearchSelect testId={`cell-faculty-${activeBatchTab}-${d}-${p._id}`} values={cFids}
                                  onChange={(vals) => setCell(activeBatchTab, d, p._id, 'faculty_ids', vals)} placeholder="Faculty (multi)"
                                  options={faculty.map(f => ({ value: f.id, label: f.short_name, sub: f.name }))} />
                              </div>
                              <Input data-testid={`cell-room-${activeBatchTab}-${d}-${p._id}`} value={c.room || ''} onChange={e => setCell(activeBatchTab, d, p._id, 'room', e.target.value)} placeholder="Room" className="h-6 text-[10px] px-1.5" />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Faculty hours preview */}
          <div>
            <h3 className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2"><Clock className="w-4 h-4 text-violet-600" />Hours Allotted (weekly, auto-calculated across all batches)</h3>
            <div className="flex flex-wrap gap-2" data-testid="tt-hours-preview">
              {Object.entries(facultyHoursPreview(builder)).map(([fid, h]) => (
                <span key={fid} className="text-xs bg-violet-50 text-violet-700 font-semibold px-2.5 py-1 rounded-full">{facName(fid)}: {h}h</span>
              ))}
              {Object.keys(facultyHoursPreview(builder)).length === 0 && <span className="text-xs text-slate-400">Assign faculty to sessions to see hours.</span>}
            </div>
          </div>
          {builder.id && <Button onClick={() => deleteTimetable(builder.id)} variant="ghost" className="text-red-500 hover:text-red-600 h-8"><Trash2 className="w-4 h-4 mr-1.5" />Delete Timetable</Button>}
        </div>
      ) : tab === 'timetables' ? (
        /* ---------------- TIMETABLE LIST ---------------- */
        <div className="space-y-3">
          <Button data-testid="tt-new" onClick={startNew} className="bg-violet-600 hover:bg-violet-700 text-white"><Plus className="w-4 h-4 mr-1.5" />New Timetable</Button>

          {timetables.length > 0 && (
            <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-3" data-testid="tt-download-panel">
              <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-2"><Download className="w-4 h-4 text-violet-600" />Download Timetable</h3>
              <div className="flex flex-wrap items-end gap-2">
                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-400 block">Timetable</label>
                  <select data-testid="dl-timetable" value={dl.tid} onChange={e => setDl({ ...dl, tid: e.target.value, batch_id: '' })} className="h-8 border border-slate-300 rounded-md px-2 text-sm min-w-[200px]">
                    <option value="">Select…</option>
                    {timetables.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-400 block">Layout</label>
                  <select data-testid="dl-layout" value={dl.layout} onChange={e => setDl({ ...dl, layout: e.target.value })} className="h-8 border border-slate-300 rounded-md px-2 text-sm">
                    <option value="batch">Per-Batch (one sheet each)</option>
                    <option value="day">Day-wise — All Batches (one sheet per day)</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-400 block">Mode</label>
                  <select data-testid="dl-mode" value={dl.mode} onChange={e => setDl({ ...dl, mode: e.target.value })} className="h-8 border border-slate-300 rounded-md px-2 text-sm">
                    <option value="week">Week-wise (day names)</option>
                    <option value="date">Date-wise (pick week)</option>
                  </select>
                </div>
                {dl.mode === 'date' && (
                  <div>
                    <label className="text-[10px] font-bold uppercase text-slate-400 block">Week of</label>
                    <Input data-testid="dl-week" type="date" value={dl.week_start} onChange={e => setDl({ ...dl, week_start: e.target.value })} className="h-8 w-36" />
                  </div>
                )}
                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-400 block">Batch filter</label>
                  <select data-testid="dl-batch" value={dl.batch_id} onChange={e => setDl({ ...dl, batch_id: e.target.value })} className="h-8 border border-slate-300 rounded-md px-2 text-sm max-w-[160px]">
                    <option value="">All batches (multi-sheet)</option>
                    {(timetables.find(t => t._id === dl.tid)?.batch_ids || []).map(bid => <option key={bid} value={bid}>{batchName(bid)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-400 block">Faculty filter</label>
                  <select data-testid="dl-faculty" value={dl.faculty_id} onChange={e => setDl({ ...dl, faculty_id: e.target.value })} className="h-8 border border-slate-300 rounded-md px-2 text-sm max-w-[160px]">
                    <option value="">All faculty</option>
                    {faculty.map(f => <option key={f.id} value={f.id}>{f.short_name}</option>)}
                  </select>
                </div>
                <Button data-testid="dl-excel" onClick={() => downloadTT('excel')} variant="outline" className="h-8"><Download className="w-4 h-4 mr-1.5" />Excel</Button>
                <Button data-testid="dl-pdf" onClick={() => downloadTT('pdf')} variant="outline" className="h-8"><Download className="w-4 h-4 mr-1.5" />PDF</Button>
              </div>
            </div>
          )}

          {timetables.length === 0 ? (
            <div className="text-center py-16"><CalendarRange className="w-10 h-10 text-slate-300 mx-auto mb-3" /><p className="text-sm text-slate-500">No timetables yet.</p></div>
          ) : (
            <div className="grid gap-2">
              {timetables.map(tt => (
                <div key={tt._id} data-testid={`tt-row-${tt._id}`} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-slate-800">{tt.name}</span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${tt.status === 'Published' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>{tt.status}</span>
                    </div>
                    <div className="text-[11px] text-slate-400">Batches: {(tt.batch_ids || []).map(batchName).join(', ') || '—'} · {(tt.entries || []).length} sessions{tt.week_start ? ` · Week of ${tt.week_start}` : ''}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button data-testid={`tt-edit-${tt._id}`} onClick={() => openEdit(tt)} variant="outline" size="sm" className="h-8"><Pencil className="w-3.5 h-3.5" /></Button>
                    {tt.status !== 'Published' && <Button data-testid={`tt-publish-${tt._id}`} onClick={() => publishFromList(tt._id)} size="sm" className="h-8 bg-violet-600 hover:bg-violet-700 text-white"><Send className="w-3.5 h-3.5" /></Button>}
                    <Button data-testid={`tt-delete-${tt._id}`} onClick={() => deleteTimetable(tt._id)} variant="ghost" size="sm" className="h-8 text-red-500 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : tab === 'hours' ? (
        /* ---------------- HOURS DASHBOARD ---------------- */
        <div className="space-y-3" data-testid="tt-hours-dashboard">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs text-slate-500 max-w-xl">Cumulative hours completed per Subject + Batch — computed only from <b>Published</b> timetables, across all weeks so far.</p>
            <div className="flex gap-2">
              <Button data-testid="hours-export-excel" onClick={() => exportHoursDashboard('excel')} variant="outline" className="h-8"><Download className="w-4 h-4 mr-1.5" />Excel</Button>
              <Button data-testid="hours-export-pdf" onClick={() => exportHoursDashboard('pdf')} variant="outline" className="h-8"><Download className="w-4 h-4 mr-1.5" />PDF</Button>
            </div>
          </div>
          {hoursDashboardLoading ? (
            <div className="text-center py-10 text-sm text-slate-400">Loading…</div>
          ) : hoursDashboard.length === 0 ? (
            <div className="text-center py-16"><Clock className="w-10 h-10 text-slate-300 mx-auto mb-3" /><p className="text-sm text-slate-500">No published timetable data yet.</p></div>
          ) : (
            <div className="overflow-x-auto border border-slate-200 rounded-lg">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="p-2.5 text-left font-bold text-slate-500 text-xs uppercase tracking-wider">Subject</th>
                    <th className="p-2.5 text-left font-bold text-slate-500 text-xs uppercase tracking-wider">Batch</th>
                    <th className="p-2.5 text-center font-bold text-slate-500 text-xs uppercase tracking-wider">Sessions</th>
                    <th className="p-2.5 text-center font-bold text-slate-500 text-xs uppercase tracking-wider">Total Hours</th>
                    <th className="p-2.5 text-center font-bold text-slate-500 text-xs uppercase tracking-wider">Weeks · Faculty</th>
                  </tr>
                </thead>
                <tbody>
                  {hoursDashboard.map((r, i) => {
                    const isOpen = expandedHoursRows.has(i);
                    const hasFaculty = (r.faculty_breakdown || []).length > 0;
                    return (
                      <React.Fragment key={i}>
                        <tr data-testid={`hours-row-${i}`} className="border-t border-slate-100 hover:bg-slate-50">
                          <td className="p-2.5"><span className="font-bold text-slate-800">{r.subject_code}</span> <span className="text-slate-400 text-xs">{r.subject_name}</span></td>
                          <td className="p-2.5 text-slate-600">{r.batch_name}</td>
                          <td className="p-2.5 text-center text-slate-600">{r.total_sessions}</td>
                          <td className="p-2.5 text-center font-bold text-violet-700">{r.total_hours}h</td>
                          <td className="p-2.5 text-center">
                            <button
                              data-testid={`hours-toggle-faculty-${i}`}
                              onClick={() => setExpandedHoursRows(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; })}
                              disabled={!hasFaculty}
                              className={`flex items-center gap-1 mx-auto font-medium ${hasFaculty ? 'text-violet-700 hover:text-violet-900 cursor-pointer' : 'text-slate-400 cursor-default'}`}
                            >
                              {r.weeks_count} {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : hasFaculty ? <ChevronDown className="w-3.5 h-3.5" /> : null}
                            </button>
                          </td>
                        </tr>
                        {isOpen && hasFaculty && (
                          <tr data-testid={`hours-faculty-breakdown-${i}`} className="bg-violet-50/40">
                            <td colSpan={5} className="p-3">
                              <p className="text-[10px] font-bold uppercase tracking-wider text-violet-700 mb-1.5">Faculty Hours Breakdown — {r.subject_code} · {r.batch_name}</p>
                              <table className="w-full text-xs border-collapse max-w-lg">
                                <thead>
                                  <tr className="bg-violet-100">
                                    <th className="text-left px-2 py-1 font-bold uppercase text-[9px] text-violet-700 border border-violet-200">Faculty</th>
                                    <th className="text-center px-2 py-1 font-bold uppercase text-[9px] text-violet-700 border border-violet-200">Sessions</th>
                                    <th className="text-center px-2 py-1 font-bold uppercase text-[9px] text-violet-700 border border-violet-200">Hours</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {r.faculty_breakdown.map((fb, fi) => (
                                    <tr key={fi} className="bg-white">
                                      <td className="px-2 py-1 border border-slate-100">{fb.faculty_name}</td>
                                      <td className="px-2 py-1 border border-slate-100 text-center">{fb.sessions}</td>
                                      <td className="px-2 py-1 border border-slate-100 text-center font-semibold text-violet-700">{fb.hours}h</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        /* ---------------- SETUP ---------------- */
        <div className="grid md:grid-cols-2 gap-6">
          {/* Session Slots */}
          <div className="space-y-2">
            <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2"><Clock className="w-4 h-4 text-violet-600" />Session Slots</h3>
            <p className="text-[11px] text-slate-400">Define once — auto-used across all timetables for this centre.</p>
            <div className="flex flex-wrap gap-2 items-end">
              <Input data-testid="period-label" value={newPeriod.label} onChange={e => setNewPeriod({ ...newPeriod, label: e.target.value })} placeholder="Label (P1)" className="h-8 w-24" />
              <Input data-testid="period-start" type="time" value={newPeriod.start_time} onChange={e => setNewPeriod({ ...newPeriod, start_time: e.target.value })} className="h-8 w-28" />
              <Input data-testid="period-end" type="time" value={newPeriod.end_time} onChange={e => setNewPeriod({ ...newPeriod, end_time: e.target.value })} className="h-8 w-28" />
              <Button data-testid="add-period" onClick={addPeriod} size="sm" className="h-8"><Plus className="w-4 h-4" /></Button>
            </div>
            <label className="flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer">
              <Checkbox data-testid="period-is-break" checked={newPeriod.is_break} onCheckedChange={v => setNewPeriod({ ...newPeriod, is_break: !!v })} />
              Mark as a break (e.g. Lunch) — shown as a fixed label across all batches, no class scheduled
            </label>
            {newPeriod.is_break && (
              <Input data-testid="period-break-label" value={newPeriod.break_label} onChange={e => setNewPeriod({ ...newPeriod, break_label: e.target.value })} placeholder="Break label" className="h-8 w-40" />
            )}
            {periods.map(p => (
              edit?.kind === 'period' && edit.id === p._id ? (
                <div key={p._id} className="flex flex-wrap items-center gap-1 border border-violet-200 rounded px-2 py-1">
                  <Input value={edit.fields.label} onChange={e => setEdit({ ...edit, fields: { ...edit.fields, label: e.target.value } })} className="h-7 w-16" />
                  <Input type="time" value={edit.fields.start_time} onChange={e => setEdit({ ...edit, fields: { ...edit.fields, start_time: e.target.value } })} className="h-7 w-24" />
                  <Input type="time" value={edit.fields.end_time} onChange={e => setEdit({ ...edit, fields: { ...edit.fields, end_time: e.target.value } })} className="h-7 w-24" />
                  <label className="flex items-center gap-1 text-[10px] text-slate-500 cursor-pointer">
                    <Checkbox checked={edit.fields.is_break} onCheckedChange={v => setEdit({ ...edit, fields: { ...edit.fields, is_break: !!v } })} /> Break
                  </label>
                  {edit.fields.is_break && (
                    <Input value={edit.fields.break_label} onChange={e => setEdit({ ...edit, fields: { ...edit.fields, break_label: e.target.value } })} className="h-7 w-24" placeholder="Label" />
                  )}
                  <button onClick={saveEdit} className="text-green-600"><Check className="w-4 h-4" /></button>
                  <button onClick={() => setEdit(null)} className="text-slate-400"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <div key={p._id} className="flex items-center justify-between text-sm border border-slate-100 rounded px-2 py-1">
                  <span><b>{p.label}</b> <span className="text-slate-400 text-xs">{p.start_time}–{p.end_time} ({hoursOf(p)}h)</span>
                    {p.is_break && <span className="ml-2 text-[10px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">BREAK · {p.break_label}</span>}
                  </span>
                  <div className="flex gap-2">
                    <button data-testid={`edit-period-${p._id}`} onClick={() => setEdit({ kind: 'period', id: p._id, fields: { label: p.label, start_time: p.start_time, end_time: p.end_time, order: p.order || 0, is_break: !!p.is_break, break_label: p.break_label || 'LUNCH' } })} className="text-slate-400 hover:text-violet-600"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={async () => { await api.ttDeletePeriod(p._id); loadAll(); }} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              )
            ))}
          </div>

          {/* Subjects */}
          <div className="space-y-2">
            <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2"><BookOpen className="w-4 h-4 text-violet-600" />Subject Shortcodes</h3>
            <div className="flex gap-2 items-end">
              <Input data-testid="subject-code" value={newSub.shortcode} onChange={e => setNewSub({ ...newSub, shortcode: e.target.value })} placeholder="Shortcode" className="h-8 w-28" />
              <Input data-testid="subject-name" value={newSub.name} onChange={e => setNewSub({ ...newSub, name: e.target.value })} placeholder="Full name (optional)" className="h-8 flex-1" />
              <Button data-testid="add-subject" onClick={addSubject} size="sm" className="h-8"><Plus className="w-4 h-4" /></Button>
            </div>
            {subjects.map(s => (
              edit?.kind === 'subject' && edit.id === s._id ? (
                <div key={s._id} className="flex items-center gap-1 border border-violet-200 rounded px-2 py-1">
                  <Input value={edit.fields.shortcode} onChange={e => setEdit({ ...edit, fields: { ...edit.fields, shortcode: e.target.value } })} className="h-7 w-24" />
                  <Input value={edit.fields.name} onChange={e => setEdit({ ...edit, fields: { ...edit.fields, name: e.target.value } })} className="h-7 flex-1" />
                  <button onClick={saveEdit} className="text-green-600"><Check className="w-4 h-4" /></button>
                  <button onClick={() => setEdit(null)} className="text-slate-400"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <div key={s._id} className="flex items-center justify-between text-sm border border-slate-100 rounded px-2 py-1">
                  <span><b>{s.shortcode}</b> <span className="text-slate-400 text-xs">{s.name}</span></span>
                  <div className="flex gap-2">
                    <button data-testid={`edit-subject-${s._id}`} onClick={() => setEdit({ kind: 'subject', id: s._id, fields: { shortcode: s.shortcode, name: s.name || '' } })} className="text-slate-400 hover:text-violet-600"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={async () => { await api.ttDeleteSubject(s._id); loadAll(); }} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              )
            ))}
          </div>

          {/* Courses */}
          <div className="space-y-2">
            <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2"><GraduationCap className="w-4 h-4 text-violet-600" />Courses</h3>
            <div className="flex gap-2 items-end">
              <Input data-testid="course-name" value={newCourse.name} onChange={e => setNewCourse({ ...newCourse, name: e.target.value })} placeholder="Course name" className="h-8 flex-1" />
              <Input data-testid="course-code" value={newCourse.code} onChange={e => setNewCourse({ ...newCourse, code: e.target.value })} placeholder="Code" className="h-8 w-24" />
              <Button data-testid="add-course" onClick={addCourse} size="sm" className="h-8"><Plus className="w-4 h-4" /></Button>
            </div>
            {courses.map(c => (
              edit?.kind === 'course' && edit.id === c._id ? (
                <div key={c._id} className="flex items-center gap-1 border border-violet-200 rounded px-2 py-1">
                  <Input value={edit.fields.name} onChange={e => setEdit({ ...edit, fields: { ...edit.fields, name: e.target.value } })} className="h-7 flex-1" />
                  <Input value={edit.fields.code} onChange={e => setEdit({ ...edit, fields: { ...edit.fields, code: e.target.value } })} className="h-7 w-20" />
                  <button onClick={saveEdit} className="text-green-600"><Check className="w-4 h-4" /></button>
                  <button onClick={() => setEdit(null)} className="text-slate-400"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <div key={c._id} className="flex items-center justify-between text-sm border border-slate-100 rounded px-2 py-1">
                  <span><b>{c.name}</b> <span className="text-slate-400 text-xs">{c.code}</span></span>
                  <div className="flex gap-2">
                    <button data-testid={`edit-course-${c._id}`} onClick={() => setEdit({ kind: 'course', id: c._id, fields: { name: c.name, code: c.code || '' } })} className="text-slate-400 hover:text-violet-600"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={async () => { if (window.confirm('Delete course?')) { await api.deleteCourse(c._id); loadAll(); } }} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              )
            ))}
          </div>

          {/* Batches */}
          <div className="space-y-2">
            <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2"><Users className="w-4 h-4 text-violet-600" />Batches</h3>
            <div className="flex gap-2 items-end">
              <Input data-testid="batch-name" value={newBatch.name} onChange={e => setNewBatch({ ...newBatch, name: e.target.value })} placeholder="Batch name" className="h-8 flex-1" />
              <select data-testid="batch-course" value={newBatch.course_id} onChange={e => setNewBatch({ ...newBatch, course_id: e.target.value })} className="h-8 border border-slate-300 rounded-md px-2 text-sm w-32">
                <option value="">Course</option>
                {courses.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
              <Button data-testid="add-batch" onClick={addBatch} size="sm" className="h-8"><Plus className="w-4 h-4" /></Button>
            </div>
            {batches.map(b => (
              edit?.kind === 'batch' && edit.id === b._id ? (
                <div key={b._id} className="flex items-center gap-1 border border-violet-200 rounded px-2 py-1">
                  <Input value={edit.fields.name} onChange={e => setEdit({ ...edit, fields: { ...edit.fields, name: e.target.value } })} className="h-7 flex-1" />
                  <select value={edit.fields.course_id || ''} onChange={e => setEdit({ ...edit, fields: { ...edit.fields, course_id: e.target.value } })} className="h-7 border border-slate-300 rounded px-1 text-xs w-28">
                    <option value="">Course</option>
                    {courses.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                  </select>
                  <button onClick={saveEdit} className="text-green-600"><Check className="w-4 h-4" /></button>
                  <button onClick={() => setEdit(null)} className="text-slate-400"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <div key={b._id} className="flex items-center justify-between text-sm border border-slate-100 rounded px-2 py-1">
                  <span><b>{b.name}</b> <span className="text-slate-400 text-xs">{courses.find(c => c._id === b.course_id)?.name || ''}</span></span>
                  <div className="flex gap-2">
                    <button data-testid={`edit-batch-${b._id}`} onClick={() => setEdit({ kind: 'batch', id: b._id, fields: { name: b.name, course_id: b.course_id || '' } })} className="text-slate-400 hover:text-violet-600"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={async () => { await api.ttDeleteBatch(b._id); loadAll(); }} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              )
            ))}
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
